import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { ShoppingBag, Search, ExternalLink, Compass, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";

function parsePrice(str) {
  if (!str) return null;
  const n = parseFloat(str.replace(/^R\$\s*/, "").replace(",", ".").replace(/[^\d.]/g, ""));
  return isNaN(n) ? null : n;
}

function calcDiscount(original, discounted) {
  const orig = parsePrice(original);
  const disc = parsePrice(discounted);
  if (!orig || !disc || disc >= orig) return null;
  return Math.round((1 - disc / orig) * 100);
}

const PROFESSIONS = [
  "Social Media", "Copywriter", "Designer Gráfico", "Editor de Vídeo",
  "Redator SEO", "Gestor de Tráfego", "Analista de Marketing Digital",
  "Especialista em Email Marketing", "Consultor de SEO",
  "Representante Comercial", "SDR (Sales Dev Rep)", "Account Executive",
  "Customer Success Manager", "Analista de Dados", "Especialista em BI",
  "Operador de CRM", "Gerente de E-commerce", "Product Manager",
  "UX Designer", "Estrategista de Conteúdo", "Especialista em Comunidades",
  "Gerente de Parcerias", "Analista de Suporte", "Social Listening Analyst",
  "Desenvolvedor Frontend", "Desenvolvedor Backend", "Desenvolvedor Full Stack",
  "Tech Lead", "QA Engineer", "Data Engineer", "DevOps Engineer",
];

const LEVEL_COLORS = {
  "Iniciante":     { bg: "#D5F9D7", text: "#008572" },
  "Intermediário": { bg: "#FEEDB4", text: "#65541B" },
  "Avançado":      { bg: "#FADBDF", text: "#892E38" },
};

function VerifiedTooltip() {
  const [visible, setVisible] = React.useState(false);
  return (
    <span className="relative inline-flex items-center flex-shrink-0"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}>
      {/* Selo */}
      <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "#00DCA4" }}>
        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
          <path d="M1 3.5L3.2 5.8L8 1" stroke="#003326" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
      {/* Tooltip */}
      {visible && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-48 px-3 py-2 rounded-xl text-xs leading-snug shadow-lg pointer-events-none"
          style={{ background: "#003326", color: "#B0FCEA", whiteSpace: "normal" }}>
          Curso verificado e indicado pela Portüs
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
            style={{ borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #003326" }} />
        </span>
      )}
    </span>
  );
}

function CourseCard({ course }) {
  const lc = LEVEL_COLORS[course.level] || { bg: "#F2F4F4", text: "#646C6F" };
  const discPct = calcDiscount(course.original_price, course.price);

  return (
    <Card className="border-0 shadow-sm flex flex-col h-full" style={{ borderRadius: "0.75rem" }}>
      {/* Thumbnail */}
      <div className="relative overflow-hidden rounded-t-xl" style={{ aspectRatio: "16/9", background: "#F2F4F4" }}>
        {course.image_url ? (
          <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="w-10 h-10 opacity-20" style={{ color: "#00664C" }} />
          </div>
        )}
        {/* Badge de desconto — canto superior esquerdo */}
        {discPct && (
          <span className="absolute top-2 left-2 px-2.5 py-1 rounded-full text-xs font-semibold shadow-md"
            style={{ background: "#00DCA4", color: "#003326" }}>
            {discPct}% de desconto
          </span>
        )}
      </div>

      <CardContent className="p-4 flex flex-col flex-1 gap-3">
        {/* Título + selo verificado */}
        <div className="flex items-start gap-1.5">
          <p className="font-semibold text-sm leading-snug line-clamp-2" style={{ color: "#2E3233", flex: "0 1 auto", minWidth: 0 }}>
            {course.title}
          </p>
          <VerifiedTooltip />
        </div>

        {/* Descrição */}
        {course.description && (
          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: "#99A6AB" }}>
            {course.description}
          </p>
        )}

        {/* Instrutor */}
        {course.instructor_name && (
          <div className="flex items-center gap-2">
            {course.instructor_photo_url ? (
              <img src={course.instructor_photo_url} alt={course.instructor_name}
                className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "#F2F4F4" }}>
                <User className="w-3 h-3" style={{ color: "#99A6AB" }} />
              </div>
            )}
            <span className="text-xs" style={{ color: "#646C6F" }}>{course.instructor_name}</span>
          </div>
        )}

        {/* Nível + Preço */}
        <div className="flex flex-col gap-2 mt-auto">
          {course.level && (
            <span className="self-start px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ background: lc.bg, color: lc.text }}>
              {course.level}
            </span>
          )}
          {course.price && (
            <div className="flex items-center gap-2 flex-wrap">
              {discPct ? (
                <>
                  <span className="text-xs line-through" style={{ color: "#99A6AB" }}>
                    {course.original_price}
                  </span>
                  <span className="text-sm font-bold" style={{ color: "#003326" }}>
                    {course.price}
                  </span>
                </>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ background: "#EAFAF6", color: "#00664C" }}>
                  {course.price}
                </span>
              )}
            </div>
          )}
        </div>

        {/* CTA */}
        <a href={course.url} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full text-sm font-bold transition-opacity hover:opacity-90"
          style={{ background: "#00DCA4", color: "#003326" }}>
          Ver curso <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </CardContent>
    </Card>
  );
}

export default function Marketplace() {
  const [courses, setCourses] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [vocationalResult, setVocationalResult] = useState(undefined); // undefined = loading
  const [loading, setLoading] = useState(true);
  const [activeProfession, setActiveProfession] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      // Buscar usuário atual
      const user = await db.auth.me();

      // Buscar vocational_result do perfil
      if (user?.email) {
        const profiles = await db.entities.UserProfile.filter({ user_email: user.email }, "-created_date", 1);
        const result = profiles[0]?.vocational_result || null;
        setVocationalResult(result);

        // Buscar cursos recomendados
        if (result) {
          const { data: recData } = await supabase
            .from("marketplace_courses")
            .select("*")
            .eq("is_published", true)
            .contains("professions", [result]);
          setRecommended(recData || []);
        }
      } else {
        setVocationalResult(null);
      }

      // Buscar todos os cursos publicados
      const all = await db.entities.MarketplaceCourse.filter({ is_published: true }, "-created_at");
      setCourses(all);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  const filtered = courses.filter(c => {
    const matchProf = activeProfession === "all" || (c.professions || []).includes(activeProfession);
    const matchSearch = !search ||
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.description?.toLowerCase().includes(search.toLowerCase()) ||
      c.instructor_name?.toLowerCase().includes(search.toLowerCase());
    return matchProf && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-10">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Guia de Formações
        </h1>
        <p className="text-sm mt-1" style={{ color: "#99A6AB" }}>
          Cursos externos selecionados para acelerar sua carreira.
        </p>
      </div>

      {/* Seção: Recomendados para você */}
      {vocationalResult === null ? (
        <div className="rounded-2xl p-6 flex items-center gap-4"
          style={{ background: "#EAFAF6", border: "1px solid #B0FCEA" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "#B0FCEA" }}>
            <Compass className="w-6 h-6" style={{ color: "#00664C" }} />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ color: "#003326" }}>
              Descubra cursos feitos para você
            </p>
            <p className="text-xs mt-0.5" style={{ color: "#00664C" }}>
              Faça o Teste Vocacional para receber recomendações personalizadas de cursos.
            </p>
          </div>
          <Link to={createPageUrl("VocationalTest")}
            className="px-4 py-2 rounded-full text-sm font-bold flex-shrink-0 hover:opacity-90 transition-opacity"
            style={{ background: "#00DCA4", color: "#003326" }}>
            Fazer teste
          </Link>
        </div>
      ) : vocationalResult && recommended.length > 0 ? (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ background: "#B0FCEA" }}>
              <Compass className="w-3.5 h-3.5" style={{ color: "#00664C" }} />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: "#003326" }}>
                Recomendados para você
              </h2>
              <p className="text-xs" style={{ color: "#99A6AB" }}>
                Com base no seu perfil: <span className="font-semibold" style={{ color: "#00664C" }}>{vocationalResult}</span>
              </p>
            </div>
          </div>

          {/* Scroll horizontal em mobile, grid em desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommended.map(c => <CourseCard key={c.id} course={c} />)}
          </div>
        </section>
      ) : null}

      {/* Seção: Todos os cursos */}
      <section>
        <h2 className="text-base font-bold mb-4" style={{ color: "#003326" }}>
          Todos os cursos
        </h2>

        {/* Filtros */}
        <div className="flex gap-3 mb-6 flex-wrap">
          {/* Busca */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#99A6AB" }} />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar cursos..." className="pl-9" />
          </div>

          {/* Profissão — dropdown */}
          <div className="relative">
            <select
              value={activeProfession}
              onChange={e => setActiveProfession(e.target.value)}
              className="appearance-none h-10 pl-3 pr-8 rounded-md text-sm font-medium border outline-none focus:ring-2 cursor-pointer"
              style={{
                background: activeProfession !== "all" ? "#B0FCEA" : "#fff",
                color: activeProfession !== "all" ? "#003326" : "#646C6F",
                borderColor: activeProfession !== "all" ? "#00DCA4" : "#E5E9EA",
                focusRingColor: "#00DCA4",
              }}
            >
              <option value="all">Todas as profissões</option>
              {PROFESSIONS.map(prof => (
                <option key={prof} value={prof}>{prof}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs"
              style={{ color: activeProfession !== "all" ? "#003326" : "#99A6AB" }}>▾</span>
          </div>
        </div>

        {/* Grid de cursos */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "#00664C" }} />
            <p className="font-medium" style={{ color: "#99A6AB" }}>
              {search || activeProfession !== "all"
                ? "Nenhum curso encontrado com esses filtros."
                : "Nenhum curso disponível ainda."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(c => <CourseCard key={c.id} course={c} />)}
          </div>
        )}
      </section>
    </div>
  );
}
